import React from 'react';
import { MoreVertical16Regular } from '@fluentui/react-icons';
import FileReadsIndicator from '../components/FileReadsIndicator';
import { Menu, MenuItem, MenuList, MenuPopover, MenuTrigger } from '@fluentui/react-components';
import { format } from 'date-fns';
import styles from '../css/vdr.module.css';
import { StatusDot, normalizeStatus } from '../components/StatusUI';
import { VDRDocumentIcon } from '../components/VDRItemIcons';

const formatDate = value => {
    if (!value) return '—';
    try {
        return format(new Date(value), 'd MMM yyyy');
    } catch {
        return '—';
    }
};

const fileExtension = name => {
    const parts = String(name || '').split('?')[0].split('.');
    return parts.length > 1 ? parts.pop() : '';
};

const InitialDocsView = ({
    files = [],
    onOpenFile,
    onOpenFileInfo,
    selectedFileId,
    projectUsersAll,
    onUploadFiles,
}) => {
    const getUserName = id => {
        if (id == null) return '—';
        const u = projectUsersAll?.find(item => item?.value?.group_member_id === id);
        return u?.value?.full_name || '—';
    };

    const inputRef = React.useRef(null);
    const cols = { gridTemplateColumns: 'minmax(180px, 2fr) 100px 120px 120px 110px 60px 36px' };

    return (
        <>
            <div className={styles.contentHeader}>
                <div>
                    <h2 className={styles.contentTitle}>Initial Project Documents</h2>
                    <p className={styles.contentDesc}>Documents uploaded during project setup.</p>
                </div>
                {onUploadFiles && (
                    <>
                        <button type="button" className={styles.primaryBtn} onClick={() => inputRef.current?.click()}>
                            Upload files
                        </button>
                        <input
                            ref={inputRef}
                            type="file"
                            multiple
                            style={{ display: 'none' }}
                            onChange={event => {
                                const picked = Array.from(event.target.files || []);
                                if (picked.length) onUploadFiles(picked);
                                event.target.value = '';
                            }}
                        />
                    </>
                )}
            </div>

            <div className={styles.table}>
                <div className={styles.tableHead} style={cols}>
                    <span>Name</span>
                    <span>Date</span>
                    <span>Uploaded</span>
                    <span>Uploaded by</span>
                    <span>Status</span>
                    <span>Reads</span>
                    <span />
                </div>

                {(files || []).map(file => {
                    const name = file.document_name || file.name || 'Untitled';
                    const status = normalizeStatus(file.indexing_status || file.status || file.index_status);

                    return (
                        <div
                            key={file.id}
                            className={`${styles.tableRow} ${selectedFileId === file.id ? styles.tableRowSelected : ''}`}
                            style={cols}
                            onClick={() => onOpenFileInfo?.(file)}
                        >
                            <div className={styles.tableRowName}>
                                <VDRDocumentIcon file={file} extension={fileExtension(name)} />
                                <span
                                    className={styles.fileRowName}
                                    role="button"
                                    tabIndex={0}
                                    onClick={e => {
                                        e.stopPropagation();
                                        onOpenFile?.(file);
                                    }}
                                    onKeyDown={e => {
                                        if (e.key === 'Enter') {
                                            e.stopPropagation();
                                            onOpenFile?.(file);
                                        }
                                    }}
                                >
                                    {name}
                                </span>
                            </div>
                            <span className={styles.subMeta}>
                                {formatDate(file.metadata_creation_date || file.document_date)}
                            </span>
                            <span className={styles.subMeta}>
                                {formatDate(file.created || file.upload_date || file.uploaded_at)}
                            </span>
                            <span className={styles.subMeta}>{file.uploaded_by_name || getUserName(file.upload_by)}</span>
                            <StatusDot status={status} />
                            <span className={styles.subMeta}>
                                <FileReadsIndicator file={file} projectUsersAll={projectUsersAll} />
                            </span>
                            <span onClick={e => e.stopPropagation()}>
                                <Menu>
                                    <MenuTrigger disableButtonEnhancement>
                                        <button type="button" className={styles.iconBtn} aria-label="More">
                                            <MoreVertical16Regular color="#989bad" />
                                        </button>
                                    </MenuTrigger>
                                    <MenuPopover>
                                        <MenuList>
                                            <MenuItem onClick={() => onOpenFile?.(file)}>Open</MenuItem>
                                            <MenuItem onClick={() => onOpenFileInfo?.(file)}>File details</MenuItem>
                                        </MenuList>
                                    </MenuPopover>
                                </Menu>
                            </span>
                        </div>
                    );
                })}

                {(!files || files.length === 0) && (
                    <div className={styles.emptyState}>No initial project documents</div>
                )}
            </div>
        </>
    );
};

export default InitialDocsView;
